import 'core-js/stable';
import 'regenerator-runtime/runtime';
import {
  ApolloClient, ApolloProvider, InMemoryCache,
} from '@apollo/client';
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ToastProvider } from '@blend-ed/blendx-ui';
import {
  APP_INIT_ERROR, APP_READY,
  getConfig,
  initialize,
  subscribe,
} from '@edx/frontend-platform';
import { AppProvider, ErrorPage } from '@edx/frontend-platform/react';
import { Helmet } from 'react-helmet';
import ReactDOM from 'react-dom';
import { useEffect } from 'react';

import messages from './i18n';

import AllRoutes from './AllRoutes';
import './index.scss';
import { CartProvider } from './context/CartContext';
import { HeaderTitleProvider } from '@blend-ed/blendx-platform';
import { initializeClarity, initializeGoogleTagManager, useFetchClarityId, useFetchGTMId } from '@blend-ed/blendx-platform/hooks';
import { HeaderBackLinkProvider } from '@blend-ed/blendx-platform';
import { getCookies } from '@edx/frontend-platform/i18n/lib';

// Create a wrapper component to handle Clarity initialization
function AppWithAnalytics({ children }) {
  const clarityProjectId = useFetchClarityId();
  const gtmId = useFetchGTMId();
  useEffect(() => {
    if (clarityProjectId) {
      initializeClarity(clarityProjectId);
    }
  }, [clarityProjectId]);

  useEffect(() => {
    if (gtmId) {
      initializeGoogleTagManager(gtmId);
    }
  }, [gtmId]);

  return children;
}

subscribe(APP_READY, () => {
  const uri = getConfig().DDN_GRAPHQL_URL;
  const iconUrl = getConfig().FAVICON_URL;

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: process.env.NODE_ENV === 'development' ? false : true,
      },
    },
  });

  const client = new ApolloClient({
    cache: new InMemoryCache(),
    uri: uri,
    credentials: 'include',
    headers: {
      'x-hasura-org-name': getCookies().get('orgName') || '',
    },
    defaultOptions: {
      watchQuery: {
        refetchOnWindowFocus: true,
      },
      query: {
        refetchOnWindowFocus: true,
      },
    },
  });

  ReactDOM.render(
    <AppProvider>
      <QueryClientProvider client={queryClient}>
        <ApolloProvider client={client}>
          <ToastProvider>
            <CartProvider>
              <HeaderTitleProvider>
                <HeaderBackLinkProvider>
                  <AppWithAnalytics>
                    <AllRoutes />
                  </AppWithAnalytics>
                </HeaderBackLinkProvider>
              </HeaderTitleProvider>
            </CartProvider>
          </ToastProvider>
        </ApolloProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </AppProvider>,
    document.getElementById('root'),
  );
});

subscribe(APP_INIT_ERROR, (error) => {
  ReactDOM.render(<ErrorPage message={error.message} />, document.getElementById('root'));
});

initialize({
  messages,
  requireAuthenticatedUser: true,
  hydrateAuthenticatedUser: true,
});
